import { type FC, useEffect, useState } from "react";

import { Container, Control } from "@snapchat/snap-design-system";

import { type RemoteApiSpec } from "../../../../../gql-generated/types";
import { Flexbox } from "../../../../../shared/components";
import { useSaveRemoteApiSpec } from "../../../../hooks";
import {
    EditRemoteApiFormik,
    mapRemoteApiFormValues,
    mapRemoteApiSpecToInitialValues,
    remoteApiSpecSchema,
} from "../../../common/schema";
import { CopyCTA } from "../../messages.generated";
import { apiCardContainer, loadableContainer } from "../ApiCard.styles";

import { controlStyle, copyButton } from "./ApiSettingsCard.styles";
import { ApiSettingsCardFooter } from "./ApiSettingsCardFooter";
import { ApiSettingsDetails } from "./ApiSettingsDetails";
import { ApiSettingsForm } from "./ApiSettingsForm";

interface ApiSettingsCard {
    spec: RemoteApiSpec;
}

export const ApiSettingsCard: FC<ApiSettingsCard> = ({ spec }) => {
    const [editMode, setEditMode] = useState(false);
    const { saveRemoteApiSpec, loading, updatedSpecId } = useSaveRemoteApiSpec();

    useEffect(() => {
        if (updatedSpecId) {
            setEditMode(false);
        }
    }, [updatedSpecId]);

    const { id } = spec;

    if (editMode) {
        return (
            <Container className={apiCardContainer}>
                <Flexbox flexDirection="column" className={loadableContainer}>
                    <Control label="ID" className={controlStyle} tooltip={"ID of the spec (auto-generated)"}>
                        {id}
                        <button
                            className={copyButton}
                            onClick={() => id && void navigator.clipboard.writeText(id)}
                            onKeyDown={() => id && void navigator.clipboard.writeText(id)}
                        >
                            <CopyCTA />
                        </button>
                    </Control>
                    <EditRemoteApiFormik
                        initialValues={mapRemoteApiSpecToInitialValues(spec)}
                        validationSchema={remoteApiSpecSchema}
                        validateOnMount={true}
                        onSubmit={(values) => {
                            void saveRemoteApiSpec(mapRemoteApiFormValues(values));
                        }}
                    >
                        {({ submitForm, errors }) => {
                            return (
                                <>
                                    <ApiSettingsForm
                                        onSubmit={() => {
                                            void submitForm();
                                        }}
                                    />
                                    <ApiSettingsCardFooter
                                        editMode={true}
                                        onEditButtonClick={() => {
                                            setEditMode(true);
                                        }}
                                        isActionDisabled={Object.keys(errors).length > 0}
                                        onAction={() => {
                                            void submitForm();
                                        }}
                                        onCancel={() => {
                                            setEditMode(false);
                                        }}
                                        isLoading={loading}
                                    />
                                </>
                            );
                        }}
                    </EditRemoteApiFormik>
                </Flexbox>
            </Container>
        );
    }

    return (
        <Container className={apiCardContainer}>
            <Flexbox flexDirection="column" className={loadableContainer}>
                <Control label="ID" className={controlStyle} tooltip={"ID of the spec (auto-generated)"}>
                    {id}
                    <button
                        className={copyButton}
                        onClick={() => id && void navigator.clipboard.writeText(id)}
                        onKeyDown={() => id && void navigator.clipboard.writeText(id)}
                    >
                        <CopyCTA />
                    </button>
                </Control>
                <ApiSettingsDetails spec={spec} />
                <ApiSettingsCardFooter
                    editMode={false}
                    onEditButtonClick={() => {
                        setEditMode(true);
                    }}
                    isActionDisabled={editMode}
                />
            </Flexbox>
        </Container>
    );
};
